import { Controller } from "@hotwired/stimulus"
import { Dropdown } from "bootstrap"

export const bootstrapDropdownOptions = {
  popperConfig(defaultBsPopperConfig) {
    // Escape overflow:auto ancestors (e.g. .full-height-minus-header in Rails Admin).
    return { ...defaultBsPopperConfig, strategy: "fixed" }
  },
}

// Dans une modale, le toggle peut se trouver tout en bas : le menu ne tient ni dessous ni
// dessus. On le laisse glisser verticalement pour rester dans la fenêtre (sa hauteur est
// bornée en CSS), plutôt que d'être rogné.
const modalDropdownOptions = {
  popperConfig(defaultBsPopperConfig) {
    const config = bootstrapDropdownOptions.popperConfig(defaultBsPopperConfig)
    return {
      ...config,
      modifiers: [
        ...(config.modifiers || []),
        { name: "preventOverflow", options: { altAxis: true, tether: false, padding: 8 } },
      ],
    }
  },
}

const MENU_MARGIN = 8
const MENU_MIN_HEIGHT = 160

// Bootstrap Dropdown data-api is not loaded globally; initialize toggles explicitly
// (required for content rendered inside Turbo Frames after the initial page load).
export default class extends Controller {
  static targets = ["toggle"]

  connect() {
    if (!this.hasToggleTarget) return

    this.inModal = Boolean(this.element.closest(".modal"))
    this.dropdown = Dropdown.getOrCreateInstance(
      this.toggleTarget,
      this.inModal ? modalDropdownOptions : bootstrapDropdownOptions,
    )
    this.fitMenu = this.fitMenu.bind(this)
    this.toggleTarget.addEventListener("shown.bs.dropdown", this.fitMenu)
  }

  disconnect() {
    if (!this.hasToggleTarget) return

    this.toggleTarget.removeEventListener("shown.bs.dropdown", this.fitMenu)
    const instance = Dropdown.getInstance(this.toggleTarget)
    if (instance) instance.dispose()
  }

  // Un menu plus long que la place sous son bouton défile au lieu de sortir de l'écran ou de
  // passer sous la barre du bas (site sur téléphone, apps natives) ; son pied collant (Effacer,
  // Appliquer des filtres) reste en vue. Mesuré une fois Popper placé, à l'image suivante.
  fitMenu() {
    const menu = this.dropdown?._menu
    if (!menu || this.inModal) return

    menu.style.maxHeight = ""
    menu.style.overflowY = ""
    requestAnimationFrame(() => {
      if (!menu.classList.contains("show")) return

      const rect = menu.getBoundingClientRect()
      const above = (menu.dataset.popperPlacement || "").startsWith("top")
      const available = (above ? rect.bottom : bottomEdge() - rect.top) - MENU_MARGIN
      if (rect.height <= available) return

      menu.style.maxHeight = `${Math.max(available, MENU_MIN_HEIGHT)}px`
      menu.style.overflowY = "auto"
      this.dropdown.update()
    })
  }
}

// Bas de la zone visible : le haut de la barre du bas du site quand elle s'affiche, moins la
// barre d'onglets native qui recouvre la page dans les apps.
function bottomEdge() {
  let edge = window.visualViewport?.height ?? window.innerHeight

  const pill = document.querySelector(".mobile-navbar__pill")?.getBoundingClientRect()
  if (pill?.height) edge = Math.min(edge, pill.top)

  if (document.body.classList.contains("turbo-native")) edge -= nativeTabBarInset()

  return edge
}

// `--turbo-native-tab-bar-inset` mêle rem et safe area : un témoin le résout en pixels.
function nativeTabBarInset() {
  const probe = document.createElement("div")
  probe.style.cssText = "position: fixed; visibility: hidden; height: var(--turbo-native-tab-bar-inset, 0px)"
  document.body.append(probe)
  const height = probe.offsetHeight
  probe.remove()
  return height
}
